"use client";

import React, { useMemo } from "react";

import { cn, dayjs } from "utils";
import { LineChartSkeleton } from "../../LineChartSkeleton";
import { PerformanceCardProps } from "./types";
import { usePerformance } from "./usePerformance";
import Translations from "@translate/components/Portfolio/AssetInsight/PerformanceCard/PerformanceCard";
import { I18nUtil } from "@utils/i18n";
import { EmptyState } from "@/src/components/Empty";

const I18n = I18nUtil.createScoped(Translations);

/**
 * @description 展示资产性能折线图的基础骨架组件。
 */
export const PerformanceCard: React.FC<PerformanceCardProps> = ({
  className,
  data,
}) => {
  const { chartRef, leftYAxis } = usePerformance({ data });

  // 判断是否需要显示加载状态
  const isLoading = useMemo(() => {
    if (!data?.dataValue || data.dataValue.length < 2) {
      return false;
    }

    // 获取倒数第二条数据
    const secondLastData = data.dataValue.at(-2);
    if (!secondLastData?.timestamp) {
      return false;
    }

    // 获取昨天0点的UTC时间戳
    const yesterdayStartUTC = dayjs()
      .utc()
      .subtract(1, "day")
      .startOf("day")
      .valueOf();

    // 判断倒数第二条数据的时间戳是否小于今天0点
    return secondLastData.timestamp < yesterdayStartUTC;
  }, [data]);
  const isEmpty = useMemo(() => {
    if (isLoading) {
      return false;
    }
    return !data || data.XAxis.length < 2;
  }, [data, isLoading]);
  return (
    <article
      aria-label="Performance Card article"
      data-openreplay-label="Performance Card article Semantic"
      id="sem-performance-card-article"
      className={cn(
        "flex flex-col gap-3 px-5 pb-5 pt-3 relative",
        "md:p-0 md:gap-4",
        className,
      )}
    >
      {isLoading && (
        <div className="absolute z-10 inset-x-5 flex flex-col gap-3 md:gap-4 before:content-[''] before:w-32 before:h-5 before:rounded-1 before:bg-background_white_overlay5 before:block">
          <LineChartSkeleton className="md:h-47.5 h-72 w-full" />
        </div>
      )}
      <header
        aria-label="Performance Card header"
        data-openreplay-label="Performance Card header Semantic"
        id="sem-performance-card-header"
        className={cn("flex items-center justify-between", {
          "opacity-0": isLoading,
        })}
      >
        <span className="body_medium_m text-content_primary">
          {I18n.t("Performance")}
        </span>
        <p className="body_medium_s text-content_primary flex gap-3">
          {leftYAxis?.left?.map((item) => (
            <span
              key={item.name}
              className={cn(
                "shrink-0 flex items-center gap-1",
                "before:content-[''] before:inline-block before:w-0.5 before:h-0.5 before:bg-[var(--color)]",
              )}
              style={{ "--color": item.color }}
            >
              {item.name}
            </span>
          ))}
        </p>
      </header>
      {isEmpty && (
        <EmptyState
          className="w-full md:h-47.5 h-72"
          description={I18n.t("The chart will be updated the next day.")}
          hideImage
        />
      )}
      {!isEmpty && (
        <div
          className={cn("w-full md:h-47.5 h-72", {
            "opacity-0": isLoading,
          })}
          ref={chartRef}
        />
      )}
    </article>
  );
};
